/**
 * LICENSE SERVICE — Qlipper License Client (server-proxied)
 *
 * As of v2.2.1, license state lives on the PC backend (backend/db/license.json),
 * not in the browser. This service just talks to the PC backend's proxy endpoints,
 * which in turn talks to https://license.qlipper.id.
 *
 * Benefits:
 *   - LAN clients (phone on same WiFi) can use the Qlipper instance without
 *     their own license activation. License is bound to the PC server.
 *   - No more CORS headaches (all requests are same-origin or to local PC).
 *   - No sensitive data in localStorage; server persists state to disk.
 */

import axios from 'axios';

const LICENSE_API = '/api/license'; // relative — hits PC backend proxy

// Status cache (populated by refreshStatus, consumed by sync isAuthenticated/etc)
let statusCache = { active: false, email: null, fingerprint: null, activatedAt: null, lastVerifiedAt: null };
let cacheLoaded = false;

// -------------------------------------------------------------------
// HTTP helpers
// -------------------------------------------------------------------

async function post(path, body = {}) {
  const response = await axios.post(`${LICENSE_API}${path}`, body, { timeout: 20000 });
  return response.data;
}

async function get(path) {
  const response = await axios.get(`${LICENSE_API}${path}`, { timeout: 15000 });
  return response.data;
}

// -------------------------------------------------------------------
// Status / Auth
// -------------------------------------------------------------------

/**
 * Fetch current license status from the PC backend. Updates local cache.
 * Returns: { active: bool, email, fingerprint, activatedAt, lastVerifiedAt }
 */
export async function refreshStatus() {
  try {
    const data = await get('/status');
    statusCache = { ...data };
    cacheLoaded = true;
    return data;
  } catch (err) {
    statusCache = { active: false, email: null, fingerprint: null };
    cacheLoaded = true;
    return statusCache;
  }
}

/**
 * Synchronous check — returns last known status. Call refreshStatus() first to populate.
 */
export function isAuthenticated() {
  return !!statusCache.active;
}

/**
 * Get the email stored on the server (from last activation).
 */
export function getStoredEmail() {
  return statusCache.email || null;
}

/**
 * Get the license key — NOT exposed from server (kept server-side only for security).
 * Returns null. Kept as function for backward compat with any caller.
 */
export function getStoredLicenseKey() {
  return null;
}

/**
 * Get the server fingerprint used for activation.
 */
export function getDeviceId() {
  return statusCache.fingerprint || null;
}

// -------------------------------------------------------------------
// Activation
// -------------------------------------------------------------------

// Human-readable activation errors, keyed by the backend's `reason` code.
// One shared map — the network-failure reason previously existed only on the
// success:false path, but the backend sends it as an HTTP 500, so users saw
// the raw "cannot_reach_license_server" code with no guidance.
const ACTIVATION_ERROR_MESSAGES = {
  email_mismatch: 'This license key does not belong to that email address.',
  key_invalid: 'License key not found. Please check it and try again.',
  key_revoked: 'This license key has been revoked.',
  max_devices_reached: 'Maximum devices (2) already activated on this license.',
  cannot_reach_license_server:
    'Can\'t reach the license server right now — your key is fine. ' +
    'Check your internet connection and click Activate again to retry.',
};

/**
 * Activate a license on this PC using email + key.
 * Server handles fingerprint generation + talks to license.qlipper.id.
 */
export async function activateWithKey(email, key) {
  try {
    const data = await post('/activate', { email, key });

    if (!data.success) {
      throw new Error(ACTIVATION_ERROR_MESSAGES[data.reason] || data.reason || 'Activation failed.');
    }

    // Refresh cache so isAuthenticated() reflects new state immediately
    await refreshStatus();
    return { valid: true, info: data };
  } catch (err) {
    if (err.response?.data?.reason) {
      throw new Error(ACTIVATION_ERROR_MESSAGES[err.response.data.reason] || err.response.data.reason);
    }
    if (err.code === 'ECONNABORTED' || err.message.includes('timeout') || err.message.includes('Network Error')) {
      throw new Error('Cannot reach Qlipper server. Make sure the app is running on your PC.');
    }
    throw err;
  }
}

/**
 * Verify current license is still valid (refreshes server-side cache).
 */
export async function verifyLicense() {
  try {
    const data = await post('/verify');
    if (data.valid) {
      await refreshStatus();
      return { valid: true, info: data };
    }
    return { valid: false, info: null, reason: data.reason || 'invalid' };
  } catch (err) {
    return { valid: false, info: null, reason: err.response?.data?.reason || err.message };
  }
}

/**
 * Deactivate the license on this PC.
 */
export async function deactivateLicense() {
  try {
    const data = await post('/deactivate');
    statusCache = { active: false, email: null, fingerprint: null };
    return data;
  } catch (err) {
    throw new Error(err.response?.data?.error || err.message || 'Deactivation failed');
  }
}

/**
 * Fetch full license info from license.qlipper.id (via PC proxy).
 */
export async function fetchLicenseInfo() {
  try {
    return await get('/info');
  } catch (err) {
    return null;
  }
}

/**
 * Logout = deactivate (server-side clears disk state).
 */
export async function logout() {
  try {
    await deactivateLicense();
  } catch { /* still clear cache */ }
  statusCache = { active: false, email: null, fingerprint: null };
}

const licenseService = {
  refreshStatus,
  isAuthenticated,
  verifyLicense,
  activateWithKey,
  deactivateLicense,
  fetchLicenseInfo,
  logout,
  getStoredEmail,
  getStoredLicenseKey,
  getDeviceId,
};

export default licenseService;
